// SPDX-License-Identifier: GPL-3.0-or-later
//
// Read/write the user profile that AI generations will draw from. Lives at
// <root>/.applify/profile.json. For Phase 5 MVP the schema is intentionally
// loose: a single `rawText` blob (paste your CV) plus optional structured
// fields. Future phases will parse structured fields from PDF uploads.

const V2_DIR = '.applify';
const PROFILE_FILE = 'profile.json';

export interface Profile {
  schemaVersion: 1;
  fullName?: string;
  email?: string;
  location?: string;
  links?: { label: string; url: string }[];
  summary?: string;
  rawText?: string;
  // Free-text preferences used by the auto-screen on capture. Describe the
  // roles you want (industries, seniority, location, IC vs management,
  // exclusions). Empty/missing disables auto-screen.
  preferences?: string;
  // Past cover letters the user has written, used as voice/framing/content
  // examples when generating a new one. Each entry is one complete letter.
  previousCovers?: string[];
  updatedAt: string;
}

export const EMPTY_PROFILE: Profile = {
  schemaVersion: 1,
  updatedAt: new Date(0).toISOString(),
};

async function getOrCreateApplifyDir(
  root: FileSystemDirectoryHandle,
): Promise<FileSystemDirectoryHandle> {
  return root.getDirectoryHandle(V2_DIR, { create: true });
}

export async function readProfile(
  root: FileSystemDirectoryHandle,
): Promise<Profile | null> {
  let applifyDir: FileSystemDirectoryHandle;
  try {
    applifyDir = await root.getDirectoryHandle(V2_DIR);
  } catch {
    return null;
  }
  let fileHandle: FileSystemFileHandle;
  try {
    fileHandle = await applifyDir.getFileHandle(PROFILE_FILE);
  } catch {
    return null;
  }
  const text = await (await fileHandle.getFile()).text();
  if (!text.trim()) return null;
  return JSON.parse(text) as Profile;
}

// Profile writes are independent of the applications.json write queue —
// keep them simple. The user only edits this from Settings, so there's no
// concurrent-write risk.
export async function writeProfile(
  root: FileSystemDirectoryHandle,
  profile: Profile,
): Promise<void> {
  const dir = await getOrCreateApplifyDir(root);
  const handle = await dir.getFileHandle(PROFILE_FILE, { create: true });
  const writable = await handle.createWritable();
  try {
    await writable.write(JSON.stringify(profile, null, 2));
  } finally {
    await writable.close();
  }
}
